import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useRef, type ReactNode } from "react";

import { orpc } from "@/client";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page-header";
import { djs } from "@/lib/djs";
import { format } from "@/lib/format";
import { FinanceiroLabels } from "@/lib/labels/financeiro";
import { cn } from "@/lib/utils";

import type { FinanceArea } from "./types";

const AREAS: { key: FinanceArea; label: string }[] = [
	{ key: "cash", label: "Caixa" },
	{ key: "overview", label: "Visão geral" },
	{ key: "receipts", label: "Recebimentos" },
	{ key: "bills", label: "Contas a pagar" },
	{ key: "bank", label: "Extrato" },
	{ key: "reconciliation", label: "Conciliação" },
];

const TITLES: Record<FinanceArea, string> = {
	cash: "Caixa do dia",
	overview: "O mês em uma página",
	receipts: "Recebimentos",
	bills: "Contas a pagar",
	bank: "Extrato bancário",
	reconciliation: "Conciliação",
};

export function FinanceShell({
	area,
	month,
	day,
	action,
	onArea,
	onMonth,
	onDay,
	children,
}: {
	area: FinanceArea;
	month: string;
	day: string;
	action?: ReactNode;
	onArea: (area: FinanceArea) => void;
	onMonth: (month: string) => void;
	onDay: (day: string) => void;
	children: ReactNode;
}) {
	const areasRef = useRef<HTMLElement>(null);
	const activeAreaRef = useRef<HTMLButtonElement>(null);
	const { data: reconciliation } = useQuery(
		orpc.financeiro.reconciliation.summary.queryOptions({
			input: { month },
			placeholderData: keepPreviousData,
		}),
	);
	const moveMonth = (amount: number) => {
		const [year, rawMonth] = month.split("-").map(Number);
		const next = new Date(year, rawMonth - 1 + amount, 1);
		onMonth(`${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, "0")}`);
	};
	const moveDay = (amount: number) => {
		onDay(djs(day, "YYYY-MM-DD").add(amount, "day").format("YYYY-MM-DD"));
	};

	useEffect(() => {
		const areas = areasRef.current;
		const activeArea = activeAreaRef.current;

		if (areas === null || activeArea === null) {
			return;
		}

		const areasBox = areas.getBoundingClientRect();
		const activeAreaBox = activeArea.getBoundingClientRect();

		areas.scrollTo({
			left:
				areas.scrollLeft +
				activeAreaBox.left -
				areasBox.left -
				(areasBox.width - activeAreaBox.width) / 2,
			behavior: "smooth",
		});
	}, [area, reconciliation?.total]);

	return (
		<div
			className="min-w-0 pb-12"
			data-component="financeiro-v2"
			data-area={area}
			data-month={month}
			data-day={day}
		>
			<PageHeader
				eyebrow="Financeiro"
				title={TITLES[area]}
				description={
					area === "cash" ? FinanceiroLabels.dayLong(day) : FinanceiroLabels.monthLongYear(month)
				}
				actions={
					<div className="flex flex-wrap items-center gap-2">
						{area === "cash" && (
							<div className="flex items-center rounded-md border border-border bg-card shadow-card">
								<Button
									variant="ghost"
									size="icon"
									data-slot="previous-day"
									aria-label="Dia anterior"
									onClick={() => moveDay(-1)}
								>
									<ChevronLeft />
								</Button>
								<span className="flex min-w-24 items-center justify-center gap-2 px-1 text-xs font-medium text-foreground">
									<CalendarDays className="size-4" />
									{day === format.today() && "Hoje"}
									{day !== format.today() && format.date(day)}
								</span>
								<Button
									variant="ghost"
									size="icon"
									data-slot="next-day"
									aria-label="Próximo dia"
									onClick={() => moveDay(1)}
								>
									<ChevronRight />
								</Button>
							</div>
						)}
						{area !== "cash" && (
							<div className="flex items-center rounded-md border border-border bg-card shadow-card">
								<Button
									variant="ghost"
									size="icon"
									data-slot="previous-month"
									aria-label="Mês anterior"
									onClick={() => moveMonth(-1)}
								>
									<ChevronLeft />
								</Button>
								<span className="flex min-w-24 items-center justify-center gap-2 px-1 text-xs font-medium text-foreground">
									<CalendarDays className="size-4" />
									{FinanceiroLabels.monthTitle(month)}
								</span>
								<Button
									variant="ghost"
									size="icon"
									data-slot="next-month"
									aria-label="Próximo mês"
									onClick={() => moveMonth(1)}
								>
									<ChevronRight />
								</Button>
							</div>
						)}
						{action}
					</div>
				}
			/>

			<div className="grid min-w-0 grid-cols-1 gap-5 lg:grid-cols-[11rem_minmax(0,1fr)] lg:items-start">
				<div className="relative -mx-4 min-w-0 border-y border-border px-4 after:pointer-events-none after:absolute after:inset-y-0 after:right-4 after:w-8 after:bg-gradient-to-l after:from-background after:to-transparent lg:sticky lg:top-20 lg:mx-0 lg:border-0 lg:p-0 lg:after:hidden">
					<nav
						ref={areasRef}
						className="flex min-w-0 gap-2 overflow-x-auto overscroll-x-contain pr-8 [scrollbar-width:none] lg:flex-col lg:gap-1 lg:overflow-visible lg:pr-0 [&::-webkit-scrollbar]:hidden"
						aria-label="Áreas do financeiro"
					>
						{AREAS.map((item) => (
							<button
								ref={area === item.key ? activeAreaRef : undefined}
								type="button"
								key={item.key}
								data-slot={`area-${item.key}`}
								data-pending={item.key === "reconciliation" ? reconciliation?.total : undefined}
								onClick={() => onArea(item.key)}
								aria-pressed={area === item.key}
								className={cn(
									"workspace-tab shrink-0 lg:w-full lg:justify-start lg:border-b-0 lg:border-l-2 lg:px-3",
									area === item.key && "border-primary text-foreground lg:bg-card",
									area !== item.key &&
										"border-transparent text-muted-foreground hover:text-foreground",
								)}
							>
								{item.label}
								{item.key === "reconciliation" &&
									reconciliation !== undefined &&
									reconciliation.total > 0 && (
										<span className="rounded-md bg-warning-soft px-1.5 py-0.5 font-mono text-xs text-warning">
											{reconciliation.total}
										</span>
									)}
							</button>
						))}
					</nav>
				</div>

				<div className="min-w-0 lg:min-h-[30rem]">{children}</div>
			</div>
		</div>
	);
}
